import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { api, setSessionToken } from '@/lib/api';
import { useAuth } from '@/lib/auth';

const BLOCKER_LABELS = {
  active_orders_as_buyer: 'tienes pedidos en curso',
  active_orders_as_seller: 'tienes ventas en curso',
  open_pqr: 'tienes PQR abiertas',
  seller_balance_pending: 'tienes saldo o retiros pendientes',
  apple_revocation_pending: 'falta revocar el acceso con Apple',
};

export const DeleteAccountSection = ({ hasPassword }) => {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data, status } = await api.post('/me/account/delete', { confirm, password: password || undefined });
      if (status === 202) {
        const why = (data.blockers || []).map((b) => BLOCKER_LABELS[b] || b).join(', ');
        toast.info(`Solicitud registrada. Nuestro equipo la revisará porque ${why}.`);
        setOpen(false);
        return;
      }
      toast.success('Tu cuenta fue eliminada.');
      setSessionToken(null);
      nav('/', { replace: true });
      await logout();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo eliminar la cuenta');
    } finally { setBusy(false); }
  };

  return (
    <section className="mt-6 border border-red-200 bg-white p-5" data-testid="profile-delete-account">
      <h2 className="font-display font-bold text-red-700 text-base uppercase tracking-wider flex items-center gap-2">
        <Trash2 className="h-4 w-4" /> Eliminar cuenta
      </h2>
      <p className="text-xs text-slate-500 mt-1">
        Se borran tus datos personales. Pedidos, pagos y facturas se conservan por obligación legal.{' '}
        <Link to="/eliminar-cuenta" className="underline" data-testid="profile-delete-account-info-link">Más información</Link>
      </p>
      <Button data-testid="profile-delete-account-btn" onClick={() => setOpen(true)} variant="outline"
              className="mt-4 rounded-none border-red-300 text-red-700 hover:bg-red-50 h-11 px-6 font-bold">
        Eliminar mi cuenta
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="rounded-none" data-testid="delete-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar tu cuenta?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer. Escribe ELIMINAR para confirmar.</AlertDialogDescription>
          </AlertDialogHeader>
          <Input data-testid="delete-account-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                 placeholder="ELIMINAR" className="rounded-none" />
          {hasPassword && (
            <Input data-testid="delete-account-password-input" type="password" value={password}
                   onChange={(e) => setPassword(e.target.value)} placeholder="Tu contraseña" className="rounded-none" />
          )}
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="delete-account-cancel-btn">Cancelar</AlertDialogCancel>
            <Button data-testid="delete-account-submit-btn" onClick={submit}
                    disabled={busy || confirm.trim().toUpperCase() !== 'ELIMINAR' || (hasPassword && !password)}
                    className="rounded-none bg-red-700 hover:bg-red-800 font-bold">
              {busy ? 'Procesando…' : 'Eliminar definitivamente'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};
